import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Typography,
  message,
} from 'antd';
import { API, Auth } from 'aws-amplify';
import React from 'react';
import { useHistory, useLocation } from 'react-router';

import { IAuthProps } from '../../pages/Authentication';
import { redirectToLogin, stopPropagation } from '../utils';
import styles from './Auth.module.css';
import AuthNote from './AuthNote';
import {
  CardWithTitle,
  EmailFormInput,
  PasswordFormInput,
  error,
  goto,
} from './utils';

enum SetupState {
  Tenant = 1,
  User = 2,
}

const SignUp: React.FC<IAuthProps> = ({
  form,
  onStateChange,
  authState,
  onAuthEvent,
}) => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const { pathname } = useLocation();
  const { push } = useHistory();
  const [setupState, setSetupState] = React.useState<SetupState>(
    SetupState.Tenant
  );
  const [tenantName, setTenantName] = React.useState<string>('');

  const signUp = React.useCallback(
    values => {
      setLoading(true);
      Auth.signUp({
        attributes: {
          'custom:tenantName': tenantName,
          'custom:tenantType': 'standard',
          email: values.email,
          family_name: values.lastName,
          name: values.firstName,
          fullName: values.fullName,
        },
        password: values.password,
        username: values.email,
      })
        .then(() => {
          setLoading(false);
          redirectToLogin(push);
          onStateChange('signIn', {});
          message.success('Check your email');
        })
        .catch(err2 => {
          setLoading(false);
          error(err2, authState, onAuthEvent);
        });
    },
    [push, tenantName, setLoading, authState, onAuthEvent, onStateChange]
  );

  const validateTenant = React.useCallback(values => {
    setLoading(true);
    API.get('publishGiide', '/tenant', {
      queryStringParameters: {
        tenantName: values.tenantName,
      },
    })
      .then(exists => {
        setLoading(false);
        if (exists) {
          Modal.confirm({
            style: { padding: 10 },
            content:
              'The tenant "' +
              values.tenantName +
              '" already exists, do you want to create another one? If no, please contact your administrator',
            onCancel: () => setTenantName(''),
            onOk: () => {
              setTenantName(values.tenantName);
              setSetupState(SetupState.User);
            },
            title: 'Tenant already exists',
          });
        } else {
          setTenantName(values.tenantName);
          setSetupState(SetupState.User);
        }
      })
      .catch(console.error);
  }, []);

  return (
    <>
      <CardWithTitle title="Create New Account">
        <Form
          form={form}
          initialValues={{ tenantName }}
          onFinish={signUp}
          className="register-form"
        >
          {setupState === SetupState.Tenant ? (
            <>
              <Form.Item
                name="tenantName"
                rules={[
                  {
                    message: 'Please input tenant name',
                    required: true,
                  },
                ]}
              >
                <Input
                  autoFocus
                  size="large"
                  placeholder="Tenant name"
                  onPressEnter={stopPropagation(validateTenant)}
                  className={styles.signupInputField}
                />
              </Form.Item>
            </>
          ) : (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    className={styles.signupFormItem}
                    name="firstName"
                    rules={[
                      {
                        message: 'Please input your first name',
                        required: true,
                      },
                    ]}
                  >
                    <Input autoFocus size="large" placeholder="First Name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    className={styles.signupFormItem}
                    name="lastName"
                    rules={[
                      { required: true, message: 'Please input last name' },
                    ]}
                  >
                    <Input size="large" placeholder="Last Name" />
                  </Form.Item>
                </Col>
              </Row>
              <EmailFormInput className={styles.signupFormItem} />
              <PasswordFormInput />
              <Typography className={styles.signupTypography}>
                * Password requires uppercase, lowercase characters and a number
              </Typography>
              <br />
            </>
          )}
          <Row justify="space-between" align="middle">
            <Col>
              <Button
                type="primary"
                shape="round"
                ghost={true}
                onClick={
                  setupState === SetupState.Tenant
                    ? goto('signIn', push, pathname, onStateChange)
                    : () => setSetupState(SetupState.Tenant)
                }
                className={styles.signupBackButton}
              >
                Back to{' '}
                {setupState === SetupState.User ? 'Tenant Name' : 'Sign In'}
              </Button>
            </Col>
            <Col>
              <Form.Item style={{ margin: 0 }}>
                <Button
                  loading={loading}
                  type="primary"
                  size="large"
                  shape="round"
                  onClick={
                    setupState === SetupState.User ? undefined : validateTenant
                  }
                  htmlType={
                    setupState === SetupState.User ? 'submit' : undefined
                  }
                  className={styles.signupCreateAccountButton}
                >
                  {setupState === SetupState.Tenant
                    ? 'Continue'
                    : 'Create Account'}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </CardWithTitle>
      <AuthNote />
    </>
  );
};

export default SignUp;
